import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminListSocialAccounts,
  adminUpsertSocialAccount,
  adminDeleteSocialAccount,
  adminSyncGhlSocialAccounts,
} from "@/lib/social-admin.functions";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const PLATFORMS = ["facebook", "instagram", "linkedin", "x", "youtube", "tiktok"];

export const Route = createFileRoute("/admin/social/accounts")({
  component: Accounts,
});

function Accounts() {
  const list = useServerFn(adminListSocialAccounts);
  const upsert = useServerFn(adminUpsertSocialAccount);
  const del = useServerFn(adminDeleteSocialAccount);
  const sync = useServerFn(adminSyncGhlSocialAccounts);
  const [syncing, setSyncing] = useState(false);
  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await sync();
      toast.success(`${r.connected} of ${r.total} channels connected` + (r.expired.length ? ` · needs reconnect in GoHighLevel: ${r.expired.join(", ")}` : ""));
      await qc.invalidateQueries({ queryKey: ["social-accts"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Sync failed");
    } finally { setSyncing(false); }
  };
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["social-accts"], queryFn: () => list() });

  const [platform, setPlatform] = useState("linkedin");
  const [handle, setHandle] = useState("");
  const [displayName, setDisplayName] = useState("");

  const add = async () => {
    if (!handle.trim()) return toast.error("Handle required");
    try {
      await upsert({ data: { platform, handle, display_name: displayName, connected: true } });
      toast.success("Account added");
      setHandle("");
      setDisplayName("");
      await qc.invalidateQueries({ queryKey: ["social-accts"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const remove = async (id: string) => {
    if (!confirm("Remove this account?")) return;
    await del({ data: { id } });
    await qc.invalidateQueries({ queryKey: ["social-accts"] });
  };

  return (
    <div>
      <SocialSubNav />
      <h1 className="font-display text-2xl font-bold">Connected accounts</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Channels connected in GoHighLevel. Sync to pull the current list — posts go only to connected channels.
      </p>
      <Button onClick={runSync} disabled={syncing} className="mt-3">{syncing ? "Syncing…" : "Sync from GoHighLevel"}</Button>

      <div className="mt-6 grid gap-4 rounded-2xl border border-border bg-card p-4 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <div>
          <Label>Platform</Label>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {PLATFORMS.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="h">Handle</Label>
          <Input id="h" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@simplyapproved" />
        </div>
        <div>
          <Label htmlFor="d">Display name</Label>
          <Input id="d" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Simply Approved Mortgages" />
        </div>
        <div className="flex items-end">
          <Button onClick={add} className="w-full">Add</Button>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {q.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> :
          ((q.data ?? []) as any[]).length === 0 ? <p className="text-sm text-muted-foreground">No accounts yet.</p> :
          ((q.data ?? []) as any[]).map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
              <div>
                <p className="text-sm font-semibold capitalize">{a.platform} — {a.handle}</p>
                <p className="text-xs text-muted-foreground">{a.display_name ?? "—"}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${a.connected ? "bg-green-500/15 text-green-700" : "bg-muted text-muted-foreground"}`}>
                  {a.connected ? "connected" : "not connected"}
                </span>
                <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive" aria-label="Remove">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}
